import React, { useEffect, useRef, useState } from 'react';
import { Table, Input, Select, Button, Space } from 'antd';
import moment from 'moment';

const { Option } = Select;

export interface RedemptionRequestRecord {
  recId: string;
  clientId: string;
  advisorId: string;
  emdId: string;
  fullName?: string;
  clientName: string;
  account: string;
  advisorName: string;
  emdFirm: string;
  amount: number;
  dateRequest: string;
  matchPeriod?: string | null;
  queueNumber: number;
  status: string;
}

export interface OptionItem {
  recId: string;
  name: string;
}

export const STATUS_OPTIONS = [
  'Pending',
  'Completed',
  'Cancelled',
  'Redeemed',
];

// Static list of EMD firms selectable in the EMD dropdown.
export const STATIC_EMD_OPTIONS = [
  'Enoch Wealth Inc.',
  'IBP Capital Inc',
  'Raisen Pacific Wealth',
  'Waverley',
  'Tyche Global Financial',
  'Heng Wealth Management Corp',
  'Higgins Investment Group Inc',
  'Equivesto',
];

interface RedemptionRequestTableProps {
  records: RedemptionRequestRecord[];
  loading?: boolean;
  editable?: boolean;
  centerHeaders?: boolean;
  clientNameWrapChars?: number;
  advisorWrapChars?: number;
  emdWrapChars?: number;
  addButtonLabel?: string;
  addDisabled?: boolean;
  advisorOptions?: OptionItem[];
  // Called when an edited row is saved. Patch contains only changed editable fields.
  onSave?: (
    recId: string,
    patch: Partial<RedemptionRequestRecord>,
  ) => Promise<boolean | void> | boolean | void;
  // When provided (Admin), an "Add" button is shown below the table. Should return
  // the recId of the newly created row so it can be opened for editing immediately.
  onAdd?: () => string | void | Promise<string | void>;
  // Called when the account value of a new row is entered, so the parent can look
  // up the partner and fill in Client Name / Advisor / EMD.
  onAccountLookup?: (
    recId: string,
    account: string,
  ) => Promise<Partial<RedemptionRequestRecord> | void> | Partial<RedemptionRequestRecord> | void;
}

// Only EMD, Status (always) and Account (new rows only) are editable.
type EditableField =
  | 'clientName'
  | 'account'
  | 'advisorName'
  | 'advisorId'
  | 'emdFirm'
  | 'emdId'
  | 'amount'
  | 'dateRequest'
  | 'status';

const formatCurrency = (value: number): string =>
  `$${(value || 0).toLocaleString('en-US')}`;

const formatDateYMD = (value?: string): string => {
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.format('YYYY-MM-DD') : value;
};

const formatMatchPeriod = (
  value: string | null | undefined,
  status: string,
): string => {
  if (
    status !== 'Matched' &&
    status !== 'Completed' &&
    status !== 'Redeemed'
  ) return '';
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.format('YYYY-MM') : '';
};

const wrapLongText = (value: string, wrapChars?: number): React.ReactNode => {
  if (!value || !wrapChars || wrapChars <= 0 || value.length <= wrapChars) {
    return value;
  }

  return (
    <div
      style={{
        maxWidth: `${wrapChars}ch`,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
      }}
      title={value}
    >
      {value}
    </div>
  );
};

// Maps a raw account number to its registered-plan type for display.
const accountType = (account?: string): string => {
  const acc = account || '';
  if (acc.startsWith('ONRS') || acc.startsWith('BCRS')) {
    return 'RRSP';
  } else if (acc.startsWith('ONRE') || acc.startsWith('BCRE')) {
    return 'RESP';
  } else if (acc.startsWith('ONRF') || acc.startsWith('BCRF')) {
    return 'RRIF';
  } else if (acc.startsWith('ONSR') || acc.startsWith('ONSR')) {
    return 'SPRRSP';
  } else if (acc.startsWith('ONTF') || acc.startsWith('BCTF')) {
    return 'TFSA';
  } else if (acc.startsWith('BCLR')) {
    return 'LIRA';
  } else {
    return 'Non-Register';
  }
};

const RedemptionRequestTable: React.FC<RedemptionRequestTableProps> = ({
  records,
  loading = false,
  editable = false,
  centerHeaders = false,
  clientNameWrapChars,
  advisorWrapChars,
  emdWrapChars,
  addButtonLabel = '+ Add Row',
  addDisabled = false,
  advisorOptions = [],
  onSave,
  onAdd,
  onAccountLookup,
}) => {
  const pageSize = 10;
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<RedemptionRequestRecord>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const saveInFlightRef = useRef<boolean>(false);
  const accountLookupInFlightRef = useRef<Promise<Partial<RedemptionRequestRecord> | void> | null>(null);
  const lastPage = Math.max(1, Math.ceil(records.length / pageSize));

  useEffect(() => {
    if (editingKey && !records.some((row) => row.recId === editingKey)) {
      setEditingKey(null);
      setDraft({});
      setSaving(false);
      saveInFlightRef.current = false;
    }

    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [records, editingKey, currentPage, lastPage]);

  const isEditing = (record: RedemptionRequestRecord) => record.recId === editingKey;

  // New (not-yet-persisted) rows allow the Account field to be entered.
  const isNewRow = (record: RedemptionRequestRecord) =>
    record.recId.startsWith('new-') || record.recId.startsWith('static-');

  const startEdit = (record: RedemptionRequestRecord) => {
    setDraft({
      clientName: record.clientName,
      account: record.account,
      advisorName: record.advisorName,
      advisorId: record.advisorId,
      emdFirm: record.emdFirm,
      emdId: record.emdId,
      amount: record.amount,
      dateRequest: formatDateYMD(record.dateRequest),
      status: record.status,
    });
    setEditingKey(record.recId);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft({});
  };

  const setField = (field: EditableField, value: any) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const lookupAccount = (record: RedemptionRequestRecord) => {
    if (!onAccountLookup) return;
    const lookupPromise = Promise.resolve(
      onAccountLookup(record.recId, (draft.account as string) || ''),
    ).then((lookupPatch) => {
      if (lookupPatch) {
        setDraft((prev) => ({ ...prev, ...lookupPatch }));
      }
      return lookupPatch;
    });
    accountLookupInFlightRef.current = lookupPromise;
    void lookupPromise.finally(() => {
      if (accountLookupInFlightRef.current === lookupPromise) {
        accountLookupInFlightRef.current = null;
      }
    });
    return lookupPromise;
  };

  const saveEdit = async (record: RedemptionRequestRecord) => {
    if (saveInFlightRef.current) return;
    if (!onSave) {
      cancelEdit();
      return;
    }
    saveInFlightRef.current = true;
    setSaving(true);
    try {
      const lookupPatch = isNewRow(record)
        ? await accountLookupInFlightRef.current
        : undefined;
      const effectiveDraft = lookupPatch ? { ...draft, ...lookupPatch } : draft;
      const editable: EditableField[] = isNewRow(record)
        ? ['clientName', 'account', 'advisorName', 'advisorId', 'emdFirm', 'emdId', 'amount', 'dateRequest', 'status']
        : ['advisorName', 'emdFirm', 'amount', 'dateRequest', 'status'];
      const patch: Partial<RedemptionRequestRecord> = {};

      const normalizedValue = (field: EditableField): any => {
        const value = effectiveDraft[field];
        if (
          isNewRow(record) &&
          (field === 'advisorName' || field === 'advisorId' || field === 'emdFirm' || field === 'emdId') &&
          (value === undefined || value === null || value === '')
        ) {
          return (record as any)[field];
        }
        return value;
      };

      editable.forEach((field) => {
        const value = normalizedValue(field);
        if (value !== undefined && value !== (record as any)[field]) {
          (patch as any)[field] = value;
        }
      });

      if (Object.keys(patch).length === 0) {
        cancelEdit();
        return;
      }

      const saved = await onSave(record.recId, patch);
      if (saved !== false) {
        cancelEdit();
      }
    } finally {
      setSaving(false);
      saveInFlightRef.current = false;
    }
  };

  const columns: any[] = [
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      onHeaderCell: () => ({
        style: { textAlign: 'left' },
      }),
      onCell: () => ({ style: { textAlign: 'left' } }),
      render: (clientName: string, record: RedemptionRequestRecord) => {
        if (editable && isEditing(record) && isNewRow(record)) {
          return (
            <Input
              value={draft.clientName as string}
              style={{ width: 180 }}
              placeholder="Enter full name"
              onChange={(e) => setField('clientName', e.target.value)}
            />
          );
        }
        return wrapLongText(clientName || '', clientNameWrapChars);
      },
    },
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (account: string, record: RedemptionRequestRecord) => {
        // Account is entered only when adding a new row; existing rows are read-only.
        if (editable && isEditing(record) && isNewRow(record)) {
          return (
            <Input
              value={draft.account as string}
              style={{ width: 140 }}
              placeholder="Enter account"
              onChange={(e) => setField('account', e.target.value)}
              onBlur={() => void lookupAccount(record)}
              onPressEnter={() => void lookupAccount(record)}
            />
          );
        }
        return accountType(account);
      },
    },
    {
      title: 'Advisor',
      dataIndex: 'advisorName',
      key: 'advisorName',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (advisorName: string, record: RedemptionRequestRecord) => {
        if (editable && isEditing(record)) {
          return (
            <Input
              value={(draft.advisorName as string) || ''}
              style={{ width: 180 }}
              placeholder="Enter advisor name"
              onChange={(e) => {
                setDraft((prev) => ({
                  ...prev,
                  advisorName: e.target.value,
                  advisorId: '',
                }));
              }}
            />
          );
        }

        return wrapLongText(advisorName || '', advisorWrapChars);
      },
    },
    {
      title: 'EMD',
      dataIndex: 'emdFirm',
      key: 'emdFirm',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (emdFirm: string, record: RedemptionRequestRecord) => {
        if (editable && isEditing(record)) {
          return (
            <Select
              value={draft.emdFirm as string}
              style={{ minWidth: 180 }}
              placeholder="Select EMD"
              onChange={(val) => {
                setDraft((prev) => ({ ...prev, emdFirm: val, emdId: '' }));
              }}
            >
              {STATIC_EMD_OPTIONS.map((firm) => (
                <Option key={firm} value={firm}>
                  {firm}
                </Option>
              ))}
            </Select>
          );
        }
        return wrapLongText(emdFirm || '', emdWrapChars);
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (amount: number, record: RedemptionRequestRecord) => {
        if (editable && isEditing(record)) {
          return (
            <Input
              value={draft.amount !== undefined ? String(draft.amount) : ''}
              style={{ width: 130 }}
              placeholder="Enter amount"
              onChange={(e) => setField('amount', e.target.value)}
            />
          );
        }
        return formatCurrency(amount);
      },
    },
    {
      title: 'Date Request',
      dataIndex: 'dateRequest',
      key: 'dateRequest',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (dateRequest: string, record: RedemptionRequestRecord) => {
        if (editable && isEditing(record)) {
          return (
            <Input
              type="date"
              value={(draft.dateRequest as string) || formatDateYMD(dateRequest)}
              style={{ width: 150 }}
              onChange={(e) => setField('dateRequest', e.target.value)}
            />
          );
        }
        return formatDateYMD(dateRequest);
      },
    },
      {
        title: 'Match Period',
        dataIndex: 'matchPeriod',
        key: 'matchPeriod',
        onHeaderCell: () => ({
          style: { textAlign: centerHeaders ? 'center' : undefined },
        }),
        render: (matchPeriod: string | null, record: RedemptionRequestRecord) =>
          formatMatchPeriod(matchPeriod, record.status),
      },
    {
      title: 'Queue Number',
      dataIndex: 'queueNumber',
      key: 'queueNumber',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (status: string, record: RedemptionRequestRecord) => {
        if (editable && isEditing(record)) {
          return (
            <Select
              value={draft.status as string}
              style={{ minWidth: 130 }}
              onChange={(val) => setField('status', val)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <Option key={opt} value={opt}>
                  {opt}
                </Option>
              ))}
            </Select>
          );
        }
        const statusColor =
          status === 'Matched'
            ? '#52c41a'
            : status === 'Pending'
              ? '#ff4d4f'
              : status === 'Complete' || status === 'Completed' || status === 'Cancel' || status === 'Cancelled'
                ? '#000000'
                : undefined;

        return <span style={{ color: statusColor }}>{status}</span>;
      },
    },
  ];

  if (editable) {
    columns.push({
      title: 'Action',
      key: 'action',
      onHeaderCell: () => ({
        style: { textAlign: centerHeaders ? 'center' : undefined },
      }),
      render: (_: any, record: RedemptionRequestRecord) => {
        const isCompleted =
          record.status === 'Completed' || record.status === 'Complete';
        const isRedeemed = record.status === 'Redeemed';
        if (isEditing(record)) {
          return (
            <Space>
              <Button
                type="link"
                size="small"
                loading={saving}
                disabled={saving}
                onClick={() => saveEdit(record)}
              >
                Save
              </Button>
              <Button type="link" size="small" onClick={cancelEdit}>
                Cancel
              </Button>
            </Space>
          );
        }
        return (
          <Button
            type="link"
            size="small"
            disabled={editingKey !== null || isCompleted || isRedeemed}
            onClick={() => startEdit(record)}
          >
            Edit
          </Button>
        );
      },
    });
  }

  const handleAdd = async () => {
    if (!onAdd) return;
    const newRecId = await onAdd();
    if (typeof newRecId === 'string') {
      setDraft({
        clientName: '',
        account: '',
        emdFirm: '',
        status: 'Pending',
      });
      setEditingKey(newRecId);
    }
  };

  const showAddFooter = !!onAdd && Math.min(currentPage, lastPage) === lastPage;

  return (
    <Table
      rowKey="recId"
      loading={loading}
      dataSource={records}
      columns={columns}
      pagination={{ pageSize, hideOnSinglePage: true }}
      onChange={(pagination) => setCurrentPage(pagination.current || 1)}
      size="middle"
      footer={
        showAddFooter
          ? () => (
              <Button
                type="dashed"
                block
                disabled={editingKey !== null || addDisabled}
                onClick={handleAdd}
              >
                {addButtonLabel}
              </Button>
            )
          : undefined
      }
    />
  );
};

export default RedemptionRequestTable;
